import type { TimeOfDay, TimeRange } from "@thalamos/common";
import {
  formatClockTime,
  formatTimeOfDay,
  parseTimeOfDay,
  TimeOfDaySchema,
} from "@thalamos/common";

/**
 * Geometry and conversion helpers for {@link AvailabilityGrid}.
 *
 * The grid works internally in "chunks" — one cell of the visible grid, keyed by
 * column and start-minute — while callers work in blocks, each a `TimeRange` in
 * a column, which is the shape availability is meaningfully read and written in.
 * Everything here is pure so the conversion either way can be tested without a
 * DOM.
 */

/** A `TimeRange` placed in a column of the grid. */
export interface AvailabilityBlock extends TimeRange {
  /**
   * Index into the grid's `columns`. The grid never interprets this, so callers
   * are free to make a column mean a weekday, a specific date, or anything else.
   */
  columnIndex: number;
}

/**
 * A point on the grid — a column together with a time within it. Times are
 * local wall clock, as everything else the grid holds is.
 */
export interface AvailabilityGridPoint {
  columnIndex: number;
  time: TimeOfDay;
}

/** A single cell: a column paired with one row of the time axis. */
export interface CellRef {
  columnIndex: number;
  rowIndex: number;
}

const MINUTES_PER_HOUR = 60;

/** Midnight, where a full-day axis begins. */
export const START_OF_DAY = TimeOfDaySchema.parse("00:00:00");

/** `HH:mm` for labels and test ids. */
export function formatClockLabel(minutesSinceMidnight: number): string {
  return formatClockTime(formatTimeOfDay(minutesSinceMidnight));
}

/** An en-dashed range, e.g. `"09:30 – 13:00"`. */
export function formatTimeRange(
  startMinutes: number,
  endMinutes: number,
): string {
  return `${formatClockLabel(startMinutes)} – ${formatClockLabel(endMinutes)}`;
}

/** A running total as `"7h 30m"`, dropping either part when it is zero. */
export function formatDuration(totalMinutes: number): string {
  if (totalMinutes === 0) {
    return "0m";
  }
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(`${String(hours)}h`);
  }
  if (minutes > 0) {
    parts.push(`${String(minutes)}m`);
  }
  return parts.join(" ");
}

/**
 * Start minute of every row on the time axis, earliest first. A trailing
 * remainder shorter than one row is dropped so no row can extend past `dayUntil`.
 */
export function buildRowStarts(
  dayFrom: TimeOfDay,
  dayUntil: TimeOfDay,
  granularityMinutes: number,
): number[] {
  if (granularityMinutes <= 0) {
    throw new Error("Grid granularity must be a positive number of minutes");
  }
  const startMinutes = parseTimeOfDay(dayFrom);
  const endMinutes = parseTimeOfDay(dayUntil);
  if (endMinutes <= startMinutes) {
    throw new Error(
      `Grid day "${dayFrom}" - "${dayUntil}" ends before it starts`,
    );
  }

  const rowStarts: number[] = [];
  for (
    let minutes = startMinutes;
    minutes + granularityMinutes <= endMinutes;
    minutes += granularityMinutes
  ) {
    rowStarts.push(minutes);
  }
  return rowStarts;
}

export function chunkKey(columnIndex: number, startMinutes: number): string {
  return `${String(columnIndex)}:${String(startMinutes)}`;
}

/**
 * The chunks a set of blocks covers, ignoring any part outside the time axis.
 *
 * A block in a column that does not exist is rejected rather than ignored:
 * chunks are only ever read back column by column, so such a block would
 * disappear the first time an edit committed, losing availability silently.
 */
export function blocksToChunkKeys(
  blocks: readonly AvailabilityBlock[],
  rowStarts: readonly number[],
  granularityMinutes: number,
  columnCount: number,
): Set<string> {
  const keys = new Set<string>();
  for (const block of blocks) {
    if (
      !Number.isInteger(block.columnIndex) ||
      block.columnIndex < 0 ||
      block.columnIndex >= columnCount
    ) {
      throw new Error(
        `Availability block refers to column ${String(block.columnIndex)}, outside the ${String(columnCount)} columns of the grid`,
      );
    }
    const blockStart = parseTimeOfDay(block.from);
    const blockEnd = parseTimeOfDay(block.until);
    for (const rowStart of rowStarts) {
      if (rowStart >= blockStart && rowStart + granularityMinutes <= blockEnd) {
        keys.add(chunkKey(block.columnIndex, rowStart));
      }
    }
  }
  return keys;
}

/**
 * Collapse selected chunks back into the fewest blocks that describe them,
 * ordered by column then time. Adjacent selected rows always merge, so a
 * caller reading the value back sees whole availability windows rather than
 * a run of quarter-hours.
 */
export function chunkKeysToBlocks(
  keys: ReadonlySet<string>,
  rowStarts: readonly number[],
  columnCount: number,
  granularityMinutes: number,
): AvailabilityBlock[] {
  const blocks: AvailabilityBlock[] = [];

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
    let runStart: number | undefined;
    let runEnd = 0;

    for (const rowStart of rowStarts) {
      if (keys.has(chunkKey(columnIndex, rowStart))) {
        runStart ??= rowStart;
        runEnd = rowStart + granularityMinutes;
        continue;
      }
      if (runStart !== undefined) {
        blocks.push({
          columnIndex,
          from: formatTimeOfDay(runStart),
          until: formatTimeOfDay(runEnd),
        });
        runStart = undefined;
      }
    }

    if (runStart !== undefined) {
      blocks.push({
        columnIndex,
        from: formatTimeOfDay(runStart),
        until: formatTimeOfDay(runEnd),
      });
    }
  }

  return blocks;
}

/**
 * The contiguous selected run containing `cell`, as row indices, or `undefined`
 * when that cell is not selected. Exists so availability can be reported back as
 * the window a doctor has offered rather than the quarter-hour under the pointer.
 */
export function findRunAt(
  keys: ReadonlySet<string>,
  cell: CellRef,
  rowStarts: readonly number[],
): { firstRowIndex: number; lastRowIndex: number } | undefined {
  const isSelected = (rowIndex: number): boolean => {
    const rowStart = rowStarts[rowIndex];
    return (
      rowStart !== undefined && keys.has(chunkKey(cell.columnIndex, rowStart))
    );
  };

  if (!isSelected(cell.rowIndex)) {
    return undefined;
  }

  let firstRowIndex = cell.rowIndex;
  while (isSelected(firstRowIndex - 1)) {
    firstRowIndex--;
  }
  let lastRowIndex = cell.rowIndex;
  while (isSelected(lastRowIndex + 1)) {
    lastRowIndex++;
  }
  return { firstRowIndex, lastRowIndex };
}

/** Total availability described by `blocks`, in minutes. */
export function countBlockMinutes(
  blocks: readonly AvailabilityBlock[],
): number {
  return blocks.reduce(
    (total, block) =>
      total + (parseTimeOfDay(block.until) - parseTimeOfDay(block.from)),
    0,
  );
}

export function chunkKeySetsMatch(
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean {
  if (left.size !== right.size) {
    return false;
  }
  for (const key of left) {
    if (!right.has(key)) {
      return false;
    }
  }
  return true;
}
